import { memo, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  MenuItem,
  Box,
} from "@mui/material";
import { Close as CloseIcon } from "components/icons";
import MDBox from "components/MDBox";
import { createTag } from "app/services/knowledge.service";

const TAG_CATEGORIES = ["DOCUMENT_TYPE", "DEPARTMENT", "PROJECT", "TECHNOLOGY", "GENERAL"];
const COLOR_OPTIONS = [
  { name: "Blue", value: "#1976d2" },
  { name: "Green", value: "#2e7d32" },
  { name: "Orange", value: "#ed6c02" },
  { name: "Red", value: "#d32f2f" },
  { name: "Purple", value: "#9c27b0" },
  { name: "Teal", value: "#00796b" },
  { name: "Pink", value: "#c2185b" },
  { name: "Grey", value: "#757575" },
];

const initialData = {
  id: null,
  tagName: "",
  category: "GENERAL",
  color: "#1976d2",
  description: "",
};

const AddTagDialog = ({ open, setOpen, onSuccess, editObject }) => {
  const [data, setData] = useState(initialData);
  const [message, setMessage] = useState("");
  const [messageOpen, setMessageOpen] = useState(false);
  const [severity, setSeverity] = useState("success");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (editObject) {
      setData({ ...editObject });
    } else {
      setData(initialData);
    }
    setMessageOpen(false);
  }, [editObject, open]);

  const validateForm = () => {
    if (!data.tagName || data.tagName.trim() === "") {
      setError("Tag name is required");
      return false;
    }
    return true;
  };

  const setError = (errorMessage) => {
    setMessage(errorMessage);
    setMessageOpen(true);
    setSeverity("error");
  };

  const setSuccess = (successMessage) => {
    setMessage(successMessage);
    setMessageOpen(true);
    setSeverity("success");
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await createTag(data);
      setSuccess("Tag created successfully");
      setTimeout(() => {
        setData(initialData);
        setMessageOpen(false);
        setOpen(false);
        onSuccess && onSuccess();
      }, 1000);
    } catch (error) {
      setError(error.message || "An error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setData(initialData);
    setMessageOpen(false);
    setOpen(false);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editObject ? "Edit Tag" : "Create Tag"}</DialogTitle>
      <DialogContent>
        <MDBox my={2}>
          <TextField
            id="tagName"
            label="Tag Name"
            fullWidth
            variant="outlined"
            value={data.tagName}
            onChange={(e) => setData({ ...data, tagName: e.target.value })}
            required
            helperText="*Required"
          />
        </MDBox>
        <MDBox my={2} display="flex" gap={2}>
          <TextField
            id="category"
            label="Category"
            fullWidth
            select
            variant="outlined"
            value={data.category}
            onChange={(e) => setData({ ...data, category: e.target.value })}
          >
            {TAG_CATEGORIES.map((c) => (
              <MenuItem key={c} value={c}>{c.replace("_", " ")}</MenuItem>
            ))}
          </TextField>
          <TextField
            id="color"
            label="Color"
            fullWidth
            select
            variant="outlined"
            value={data.color}
            onChange={(e) => setData({ ...data, color: e.target.value })}
          >
            {COLOR_OPTIONS.map((c) => (
              <MenuItem key={c.value} value={c.value}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box sx={{ width: 16, height: 16, borderRadius: 1, backgroundColor: c.value }} />
                  {c.name}
                </Box>
              </MenuItem>
            ))}
          </TextField>
        </MDBox>
        <MDBox my={2}>
          <TextField
            id="description"
            label="Description"
            fullWidth
            multiline
            rows={2}
            variant="outlined"
            value={data.description}
            onChange={(e) => setData({ ...data, description: e.target.value })}
          />
        </MDBox>
        <Collapse in={messageOpen}>
          <Alert
            severity={severity}
            action={
              <IconButton size="small" onClick={() => setMessageOpen(false)}>
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {message}
          </Alert>
        </Collapse>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSubmit} disabled={isSubmitting} variant="contained">
          {editObject ? "Update" : "Create"}
        </Button>
        <Button onClick={handleClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(AddTagDialog);

